import { useTheme } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import {
  AlertsFromProvider,
  BCRContentContainer,
  BCRSuspense,
  LoadingFromProvider,
  useDarkMode,
} from "best-common-react";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Helmet } from "react-helmet";
import { BrowserRouter as Router, useHistory, useLocation } from "react-router-dom";
import BlockingPrompt from "../components/control/BlockingPrompt";
import { useAuth } from "../context/AuthContext";
import { getItem, removeItem } from "../util/LocalStorageUtil";

const AuthenticatedApp = React.lazy(() => import("./AuthenticatedApp"));
const UnauthenticatedApp = React.lazy(() => import("./UnauthenticatedApp"));

type AppSwitchProps = {
  loggedIn: boolean;
};

const BaseUrl = "/";

const AppSwitch: React.FC<AppSwitchProps> = ({ loggedIn }) => {
  const location = useLocation();
  const history = useHistory();
  const [deepLink, setDeepLink] = useState(BaseUrl);
  const { setIsDarkMode } = useDarkMode();

  useEffect(() => {
    // TODO: remove eventually once enough users have updated mobile app
    const oldDplTheme = getItem("dpl-theme");
    if (oldDplTheme) {
      setIsDarkMode(oldDplTheme === "dark");
      removeItem("dpl-theme");
    }
  }, []);

  useEffect(() => {
    if (!loggedIn) {
      if (location.pathname !== BaseUrl) {
        setDeepLink(location.pathname + location.search);
      }
    } else if (deepLink !== BaseUrl) {
      history.push(deepLink);
      setDeepLink(BaseUrl);
    }
  }, [loggedIn, deepLink]);

  return <BCRContentContainer>{loggedIn ? <AuthenticatedApp /> : <UnauthenticatedApp />}</BCRContentContainer>;
};

const App: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const loggedIn = !!user?.orgId;
  return (
    <React.Suspense fallback={<BCRSuspense />}>
      <Helmet>
        <title>Prospect Link</title>
      </Helmet>
      <Router
        getUserConfirmation={(message, callback) => {
          const container = document.createElement("div");
          document.body.appendChild(container);

          const onClose = (value: boolean) => {
            ReactDOM.unmountComponentAtNode(container);
            callback(value);
          };

          ReactDOM.render(
            <ThemeProvider theme={theme}>
              <BlockingPrompt blockFunc={onClose} message={message} />
            </ThemeProvider>,
            container,
          );
        }}
      >
        <AppSwitch loggedIn={loggedIn} />
      </Router>
      <AlertsFromProvider />
      <LoadingFromProvider />
    </React.Suspense>
  );
};

export default App;
